// packages/components/src/tool-management/ToolOverridesTab.tsx

import React, { useMemo, useState, useEffect } from 'react';
import useSWR from 'swr';
import { Card, Spinner, Alert, Button, TextareaField, Modal, IconButton } from '@dopomogai/ui';
import { Link as LinkIcon, Users, UserPlus, RefreshCw, Save, XCircle, Info, ExternalLink } from 'lucide-react';
import { useAgents } from '@dopomogai/agent-kit';
import { useToolTemplateOverride } from '@dopomogai/agent-kit';
import { agentService, isApiError } from '@dopomogai/api-client';
import type { AgentResponse, UUID } from '@dopomogai/types';
import { toast } from '@dopomogai/ui';

type ToolsMap = Record<UUID, string[]>;

interface ToolOverridesTabProps {
  toolSlug: string | null;
}

/**
 * Fetches the tool slug lists for all agents.
 */
async function fetchAgentToolsMap(agentList: AgentResponse[]): Promise<ToolsMap> {
  const map: ToolsMap = {};
  await Promise.all(
    agentList.map(async (a) => {
      const res = await agentService.getAgentTools(a.id);
      if (isApiError(res)) {
        // Soft-fail per agent; leave it empty if request fails
        map[a.id] = [];
        return;
      }
      map[a.id] = Array.isArray(res) ? res : [];
    })
  );
  return map;
}

/**
 * Sub-component that handles a single agent's override UI.
 * Uses useToolTemplateOverride(agentId, toolSlug) under the hood.
 */
const AgentOverrideEditor: React.FC<{
  agent: AgentResponse;
  toolSlug: string;
}> = ({ agent, toolSlug }) => {
  const { effective_template, override_template, base_template, isLoading, error, update, mutate } =
    useToolTemplateOverride(agent.id, toolSlug);
  const [value, setValue] = useState<string>(override_template ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(override_template ?? '');
  }, [override_template]);

  const onSave = async () => {
    setSaving(true);
    try {
      await update(value || null);
      toast.success('Override saved.');
      await mutate();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save override.');
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    setSaving(true);
    try {
      await update(null);
      toast.success('Override cleared.');
      await mutate();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to clear override.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card noPadding={false} className="bg-white">
        <div className="py-6 flex items-center justify-center">
          <Spinner text={`Loading override for ${agent.name}...`} />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card noPadding={false} className="bg-white">
        <Alert type="error" title={`Failed to load ${agent.name}`} message={error.message} />
      </Card>
    );
  }

  return (
    <Card noPadding={false} className="bg-white border-2 border-black">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">{agent.name}</p>
          <p className="text-xs text-slate-500">Agent ID: <span className="font-mono">{agent.id}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <a href={`/app/agents/${agent.id}`} target="_blank" rel="noreferrer">
              Open Agent <ExternalLink size={14} className="ml-1" />
            </a>
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <TextareaField
            label="Override template (optional)"
            placeholder="Provide a per-agent override for this tool template..."
            rows={8}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="primary"
              size="sm"
              onClick={onSave}
              disabled={saving}
              icon={<Save size={14} />}
            >
              {saving ? 'Saving…' : 'Save override'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onClear}
              disabled={saving || !override_template}
              icon={<XCircle size={14} />}
            >
              Clear override
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="p-3 border rounded-lg bg-slate-50">
            <p className="text-xs uppercase font-semibold text-slate-500">Effective template</p>
            <pre className="mt-1 text-xs text-slate-800 whitespace-pre-wrap break-words">
              {effective_template ?? '(none)'}
            </pre>
          </div>
          <div className="p-3 border rounded-lg bg-slate-50">
            <p className="text-xs uppercase font-semibold text-slate-500">Base template</p>
            <pre className="mt-1 text-xs text-slate-800 whitespace-pre-wrap break-words">
              {base_template ?? '(none)'}
            </pre>
          </div>
        </div>
      </div>
    </Card>
  );
};

const LinkAgentsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  toolSlug: string;
  notLinkedAgents: AgentResponse[];
  onLinked: () => void;
}> = ({ isOpen, onClose, toolSlug, notLinkedAgents, onLinked }) => {
  const [selected, setSelected] = useState<Record<UUID, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelected({});
      setErrorText(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const toggle = (agentId: UUID) => {
    setSelected(prev => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k as UUID),
    [selected]
  );

  const linkSelected = async () => {
    if (selectedIds.length === 0) {
      setErrorText('Please select at least one agent.');
      return;
    }
    setSubmitting(true);
    setErrorText(null);
    try {
      await Promise.all(
        selectedIds.map(async (agentId: UUID) => {
          const current = await agentService.getAgentTools(agentId);
          const currentSlugs = isApiError(current) ? [] : current;
          const next = Array.from(new Set([...(currentSlugs || []), toolSlug]));
          const res = await agentService.updateAgentTools(agentId, next);
          if (isApiError(res)) throw new Error(res.message || `Failed to link agent ${agentId}`);
        })
      );
      toast.success('Agents linked.');
      onLinked();
      onClose();
    } catch (e: any) {
      setErrorText(e?.message || 'Failed to link agents.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title="Link agents to this tool"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={linkSelected} disabled={submitting}>
            {submitting ? 'Linking…' : 'Link selected'}
          </Button>
        </div>
      }
    >
      {notLinkedAgents.length === 0 ? (
        <div className="py-6 text-sm text-slate-600">
          All your agents are already linked to this tool.
        </div>
      ) : (
        <div className="space-y-3">
          {errorText && <Alert type="error" message={errorText} />}
          <p className="text-sm text-slate-600">
            Select the agents you want to link to <span className="font-semibold">{toolSlug}</span>. This allows setting per‑agent overrides.
          </p>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
            {notLinkedAgents.map(a => (
              <label
                key={a.id}
                className="flex items-center justify-between p-2 border rounded-lg bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                  <p className="text-xs font-mono text-slate-500 truncate">{a.id}</p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!selected[a.id]}
                  onChange={() => toggle(a.id)}
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export const ToolOverridesTab: React.FC<ToolOverridesTabProps> = ({ toolSlug }) => {
  const { agents, isLoading: isAgentsLoading, error: agentsError } = useAgents();
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  // Load tools linked per agent when we have agents and a toolSlug
  const agentIdsKey = useMemo(
    () => (agents ? agents.map(a => a.id).sort().join(',') : ''),
    [agents]
  );
  const { data: toolsByAgent, error: toolsMapError, isLoading: isToolsMapLoading, mutate } = useSWR<ToolsMap>(
    toolSlug && agents ? ['tool-overrides/agent-tools', toolSlug, agentIdsKey] : null,
    () => fetchAgentToolsMap(agents || [])
  );

  const linkedAgents = useMemo<AgentResponse[]>(() => {
    if (!agents || !toolsByAgent || !toolSlug) return [];
    return agents.filter(a => (toolsByAgent[a.id] || []).includes(toolSlug));
  }, [agents, toolsByAgent, toolSlug]);

  const notLinkedAgents = useMemo<AgentResponse[]>(() => {
    if (!agents || !toolsByAgent || !toolSlug) return [];
    return agents.filter(a => !((toolsByAgent[a.id] || []).includes(toolSlug)));
  }, [agents, toolsByAgent, toolSlug]);

  const onLinked = async () => {
    await mutate();
  };

  if (!toolSlug) {
    return (
      <Card className="bg-white" noPadding={false}>
        <Alert
          type="info"
          title="Save your tool first"
          message="You need to save this tool (with a slug) before you can set per‑agent overrides."
        />
      </Card>
    );
  }

  if (isAgentsLoading) {
    return (
      <Card className="bg-white" noPadding={false}>
        <div className="py-8 flex items-center justify-center">
          <Spinner text="Loading agents..." />
        </div>
      </Card>
    );
  }

  if (agentsError) {
    return (
      <Card className="bg-white" noPadding={false}>
        <Alert type="error" title="Failed to load agents" message={agentsError.message} />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card
        title="Linked agents"
        icon={<Users size={18} />}
        headerBgColor="bg-slate-100"
        className="border-2 border-black"
        actionButton={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={<UserPlus size={14} />}
              onClick={() => setLinkModalOpen(true)}
            >
              Link more agents
            </Button>
            <IconButton
              ariaLabel="Refresh"
              icon={<RefreshCw size={16} />}
              variant="ghost"
              onClick={() => mutate()}
            />
          </div>
        }
      >
        {isToolsMapLoading ? (
          <div className="py-8 flex items-center justify-center">
            <Spinner text="Fetching agent links..." />
          </div>
        ) : toolsMapError ? (
          <Alert
            type="warning"
            title="Some agent-tool links could not be loaded"
            message="You can retry or proceed to link agents."
          />
        ) : linkedAgents.length === 0 ? (
          <div className="py-8">
            <Alert
              type="info"
              title="This tool is not linked to any agents yet."
              message="Link one or more agents to enable per‑agent overrides."
            />
            <div className="mt-3">
              <Button variant="primary" onClick={() => setLinkModalOpen(true)} icon={<LinkIcon size={14} />}>
                Link agents
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {linkedAgents.map(a => (
              <AgentOverrideEditor key={a.id} agent={a} toolSlug={toolSlug} />
            ))}
          </div>
        )}
      </Card>

      <Card className="bg-white border-2 border-black">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-slate-600 mt-0.5" />
          <p className="text-sm text-slate-600">
            The override endpoint requires the tool to be linked to an agent. This panel shows only agents where the tool is linked.
            Use “Link more agents” to add the tool to additional agents before setting overrides.
          </p>
        </div>
      </Card>

      <LinkAgentsModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        toolSlug={toolSlug}
        notLinkedAgents={notLinkedAgents}
        onLinked={onLinked}
      />
    </div>
  );
};

export default ToolOverridesTab;